import React, { useState, useEffect } from 'react';
import { CalendarOutlined, ClockCircleOutlined, CloseCircleOutlined, UserOutlined, CheckCircleOutlined, MedicineBoxOutlined, FrownOutlined, } from '@ant-design/icons';
import { format, subDays, differenceInDays, isPast } from 'date-fns';
import { Avatar, Badge, Card, List, Row, Col, Typography, Alert } from 'antd';
import '../../../styles/Dashboard.css';
import { USERURL, ACCOUNTURL } from "../config/config";
import { toast } from "react-toastify";
import { Fetchdetails } from "../../fetchdetails/fetchdetails";

const { Title, Text } = Typography;

const getCurrentTimesheetPeriod = () => {
  const today = new Date();
  const startOfWeek = subDays(today, today.getDay() - 1);
  const endOfWeek = subDays(startOfWeek, -6);

  return { start: startOfWeek, end: endOfWeek };
};

const Dashboard = () => {
  const [userDetails, setUserDetails] = useState(null);

  const [showSickLeave, setShowSickLeave] = useState(false);
  const [loading, setLoading] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [sickLeaveCount, setSickLeaveCount] = useState("")
  const [timesheetData, setTimesheetData] = useState([]);

  const candidateId = localStorage.getItem('candidate_id')
  const [declineTsList, setDeclineTsList] = useState([])


  const { start, end } = getCurrentTimesheetPeriod();
  const timesheetPeriod = `${format(start, 'MMM dd,')} - ${format(end, 'MMM dd,')}`;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const userDetails = JSON.parse(localStorage.getItem('userdetails') || '{}');

        setUserDetails({
          name: `${userDetails.fullname}`.trim(),
          email: `${userDetails.email}`,
          designation: `${userDetails.designation}`,
          avatar: 'https://zos.alipayobjects.com/rmsportal/ODTLcjxAfvqbxHnVXCYX.png'
        });

        const specialOption = userDetails?.special_option;
        if (specialOption === 'yes') {
          setShowSickLeave(true);
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchWeekList = async (id) => {
      if (!id) {
        console.warn("candidate_id not found");
        return;
      }

      const token = localStorage.getItem('access_token');
      const api = `${ACCOUNTURL}getcandidateweeklist/${id}`;

      const postData = {
        method: 'get',
        headers: {
          'content-type': 'application/json',
          'authorization': token
        },
      };

      try {
        const response = await Fetchdetails(api, postData);

        if (response?.actionMsg === 1 && response?.res?.actionMsg === 1) {
          const weekData = response?.res?.data;
          setTimesheetData(weekData);


          setSickLeaveCount(response?.res?.sickleave)

        } else {
          console.error("Failed to fetch week list");
          toast.error('Failed to fetch week list');
        }
      } catch (error) {
        console.error("Error fetching week list:", error);
        toast.error('Error fetching week list');
      }
    };


    const getEditedList = async (id) => {
      if (!id) {
        console.warn("candidate_id not found in local storage.");
        toast.warn("Candidate ID not found.", { duration: 2000 });
        return;
      }

      const token = localStorage.getItem('access_token');
      const api = `${ACCOUNTURL}gettseditlist/${id}`;

      const postData = {
        method: 'get',
        headers: {
          'content-type': 'application/json',
          'authorization': token
        },
      };

      const response = await Fetchdetails(api, postData);
      setDeclineTsList(response?.res?.datalist)
      if (response?.actionMsg === 1 && response?.res?.actionMsg === 1) {
        toast.success(response.res.message)

      } else {
        console.error("Failed to fetch week list:", response?.res?.Message || "Contact admin");
        toast.error(response?.res?.Message || 'Failed to fetch week list', { duration: 2000 });
        setPeriods([]);
      }
    };


    fetchData();
    fetchWeekList(candidateId);
    getEditedList(candidateId)
  }, []);

  const isDateInPeriod = (date, start, end) => {
    const checkDate = new Date(date);
    const startDate = new Date(start);
    const endDate = new Date(end);
    return checkDate >= startDate && checkDate <= endDate;
  };

  const getPeriodStatus = (period) => {
    const today = new Date();
    const startDate = new Date(period.weekstart);
    const endDate = new Date(period.weekend);

    if (isDateInPeriod(today, period.weekstart, period.weekend)) {
      return 'current';
    } else if (today < startDate) {
      return 'future';
    }
    return 'past';
  };

  return (
    <>
      <title>Dashboard</title>
      <div className="dashboard-container">
      <div className="dashboard-inner">
        <Title level={2} className="dashboard-title">Dashboard</Title>

        <Row gutter={[24, 24]}>
          <Col xs={24} md={12} lg={6}>
            <Card
              className="custom-card user-details-card"
              title={<div><UserOutlined /> User Details</div>}
              loading={loading}
            >
              {userDetails ? (
                <div className="user-details">
                  <Avatar
                    size={64}
                    src={userDetails.avatar}
                    icon={!userDetails.avatar && <UserOutlined />}
                    alt="User Avatar"
                    className="user-avatar"
                  />
                  <div>
                    <div className="user-name">{userDetails.name || 'N/A'}</div>
                    <div className="user-department">Department: {userDetails.designation || 'N/A'}</div>
                    <div className="user-email">{userDetails.email || 'N/A'}</div>
                  </div>
                </div>
              ) : (
                <div className="user-details-placeholder">
                  <p>No user details available</p>
                </div>
              )}
            </Card>
          </Col>

          <Col xs={24} md={12} lg={18}>
            <Card
              className="custom-card timesheet-period-card"
              title={<div><ClockCircleOutlined /> Timesheet Period</div>}
              loading={loading}
            >
              {timesheetData && timesheetData.length > 0 && (
                <Row gutter={[8, 8]} className="timesheet-period-row">
                  <Col xs={24} sm={8}>
                    <Card className="period-stat-card" size="small" bordered>
                      <div className="period-stat-content">
                        <Text type="secondary">Week Start</Text>
                        <Text strong>{timesheetData[0]?.label || 'N/A'}</Text>
                      </div>
                    </Card>
                  </Col>
                  {timesheetData.map((item, index) => {
                    if (item.fstatus === 'Current') {
                      return (
                        <Col xs={24} sm={8} key={index}>
                          <Card className="period-stat-card current" size="small" bordered>
                            <div className="period-stat-content">
                              <Text type="secondary">Current Week</Text>
                              <Text strong style={{ color: '#1890ff' }}>
                                {item.label}
                              </Text>
                            </div>
                          </Card>
                        </Col>
                      );
                    }
                    return null;
                  })}
                  <Col xs={24} sm={8}>
                    <Card className="period-stat-card" size="small" bordered>
                      <div className="period-stat-content">
                        <Text type="secondary">Week End</Text>
                        <Text strong>{timesheetData[timesheetData.length - 1]?.label || 'N/A'}</Text>
                      </div>
                    </Card>
                  </Col>
                </Row>
              )}
              {(!timesheetData || timesheetData.length === 0) && (
                <div className="no-current-period">
                  <Text type="secondary">No timesheet period available</Text>
                </div>
              )}
            </Card>
          </Col>
        </Row>

        <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
          <Col xs={24} md={12} lg={18} className="declined-timesheets-col">
            <Card
              className="custom-card declined-list"
              title={<div><CloseCircleOutlined /> Declined Timesheets ({declineTsList?.length || 0})</div>}
              loading={loading}
            >
              {declineTsList?.length === 0 ? (
                <div className="no-declined">
                  <FrownOutlined />
                  <p>No declined timesheets.</p>
                </div>
              ) : (
                <Row gutter={[16, 16]}>
                  {declineTsList?.map((item) => (
                    <Col xs={24} sm={12} key={item.autoId}>
                      <Card
                        className="declined-timesheet-card"
                        size="small"
                        bordered
                      >
                        <div className="declined-timesheet-content">
                          <div className="timesheet-header">
                            <CalendarOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                            <Text strong>Timesheet Period</Text>
                          </div>
                          <div className="timesheet-period">
                            <Text>{item.weeks}</Text>
                          </div>
                          <div className="timesheet-hours-tag">
                            <Badge
                              count={`${item.hours} Hours`}
                              style={{
                                backgroundColor: '#1890ff',
                                fontSize: '12px',
                                padding: '0 8px'
                              }}
                            />
                          </div>
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Card>
          </Col>


          <Col xs={24} md={12} lg={6} className="quick-stats-col">
            <Card className="quick-stats-card1" bordered>
              <h3 style={{ textAlign: 'center', marginBottom: '16px' }}>Quick Status</h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px'
              }}>
                {showSickLeave ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    maxWidth: '250px',
                    padding: '8px 16px',
                    border: '1px solid #f0f0f0',
                    borderRadius: '8px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <MedicineBoxOutlined style={{ color: '#cf1322', marginRight: 8 }} />
                      <span>Sick Leave Days</span>
                    </div>
                    <span style={{
                      color: '#cf1322',
                      fontWeight: 'bold',
                      fontSize: '16px'
                    }}>
                      {sickLeaveCount || "0"}
                    </span>
                  </div>
                ) : (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    maxWidth: '250px',
                    padding: '8px 16px',
                    border: '1px solid #f0f0f0',
                    borderRadius: '8px',
                    color: '#8c8c8c'
                  }}>
                    No sick leave available
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
    </>
    
  );
};

export default Dashboard;
